import { cn } from '@quadralize/utils'
import * as TogglePrimitive from '@radix-ui/react-toggle'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const toggleVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-primary/10 data-[state=on]:text-primary',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-border-default bg-transparent hover:bg-bg-subtle hover:text-text-primary',
      },
      size: {
        default: 'h-9 px-3',
        sm: 'h-8 px-2',
        lg: 'h-10 px-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const Toggle = React.forwardRef<
  React.ComponentRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
))

Toggle.displayName = TogglePrimitive.Root.displayName

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    orientation?: 'horizontal' | 'vertical'
    spacing?: boolean
  }
>({
  size: 'default',
  variant: 'default',
  orientation: 'horizontal',
  spacing: false,
})

const ToggleGroup = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants> & {
      orientation?: 'horizontal' | 'vertical'
      spacing?: boolean
    }
>(
  (
    { className, variant, size, orientation = 'horizontal', spacing = false, children, ...props },
    ref,
  ) => (
    <ToggleGroupPrimitive.Root
      ref={ref}
      orientation={orientation}
      className={cn(
        'inline-flex m-0 p-0 border-none min-w-0 h-fit w-fit items-center',
        orientation === 'vertical' ? 'flex-col' : 'flex-row',
        !spacing && [
          // Rounded corners logic for all children
          '*:rounded-none',
          orientation === 'horizontal'
            ? '[&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md'
            : '[&>*:first-child]:rounded-t-md [&>*:last-child]:rounded-b-md',
          // Border merging logic
          orientation === 'horizontal' ? '[&>*+*]:-ml-px' : '[&>*+*]:-mt-px',
        ],
        spacing && 'gap-2',
        // Ensure borders and focus rings of active/hovered items are on top
        '*:relative *:hover:z-10 *:focus-within:z-10 data-[state=on]:z-10',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, orientation, spacing }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  ),
)

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext)

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export { Toggle, ToggleGroup, ToggleGroupItem, toggleVariants }
